<script setup lang="ts">
  defineOptions({ name: 'CPButton', inheritAttrs: false })

  import { computed, type PropType } from 'vue'

  import { HLButton } from '@platform-ui/highrise'

  import type { HLButtonVariant, HLCommonSize } from '@platform-ui/highrise'

  const props = defineProps({
    type: {
      type: String as PropType<HLButtonVariant>,
      default: 'primary',
    },
    id: {
      type: String,
      required: true,
    },
    customStyle: {
      type: String,
      default: '',
    },
    color: {
      type: String as PropType<'primary' | 'secondary' | 'tertiary' | 'text' | 'error' | 'custom' | 'transparent' | 'success'>,
      default: 'primary',
    },
    customTextColor: {
      type: String,
      default: undefined,
    },
    customBackgroundColor: {
      type: String,
      default: undefined,
    },
    customBorderColor: {
      type: String,
      default: undefined,
    },
    customHoverTextColor: {
      type: String,
      default: undefined,
    },
    customHoverBackgroundColor: {
      type: String,
      default: undefined,
    },
    customHoverBorderColor: {
      type: String,
      default: undefined,
    },
    link: {
      type: Boolean,
      default: false,
    },
    // variant: {
    //   type: String as PropType<'primary' | 'secondary' | 'tertiary' | 'text' | 'ghost'>,
    //   default: 'primary',
    // },
    size: {
      type: String as PropType<HLCommonSize | 'xl'>,
      default: 'xs',
    },
  })

  const buttonStyles = computed(() => {
    const baseStyles = props.customStyle || ''

    // Handle custom color type
    if (props.color === 'custom') {
      const customStyles: Record<string, string> = {}

      if (props.customBackgroundColor) {
        customStyles['--n-color'] = props.customBackgroundColor
        customStyles['--n-color-focus'] = 'transparent'
      }
      if (props.customHoverBackgroundColor) {
        customStyles['--n-color-pressed'] = props.customHoverBackgroundColor
        customStyles['--n-color-hover'] = props.customHoverBackgroundColor
      } else {
        customStyles['--n-color-pressed'] = 'transparent'
      }
      if (props.customTextColor) {
        customStyles['--n-text-color'] = props.customTextColor
        customStyles['--n-text-color-pressed'] = props.customTextColor
        customStyles['--n-text-color-focus'] = props.customTextColor
      }
      if (props.customHoverTextColor) {
        customStyles['--n-text-color-hover'] = props.customHoverTextColor
      }
      if (props.customBorderColor) {
        customStyles['--n-border'] = `1px solid ${props.customBorderColor}`
        customStyles['--n-border-pressed'] = `1px solid ${props.customBorderColor}`
        customStyles['--n-border-focus'] = `1px solid ${props.customBorderColor}`
      }
      if (props.customHoverBorderColor) {
        customStyles['--n-border-hover'] = `1px solid ${props.customHoverBorderColor}`
      }

      const customStyleString = Object.entries(customStyles)
        .map(([key, value]) => `${key}: ${value}`)
        .join('; ')

      return `${baseStyles}; ${customStyleString}`
    }

    const typeStyles = {
      primary:
        props.color === 'error'
          ? {
              '--n-color': 'var(--cp-error-500)',
              '--n-color-hover': 'var(--cp-error-600)',
              '--n-color-pressed': 'var(--cp-error-700)',
              '--n-color-focus': 'var(--cp-error-500)',
              '--n-color-disabled': 'var(--cp-error-100)',
              '--n-text-color': 'var(--cp-text-on-error)',
              '--n-text-color-hover': 'var(--cp-text-on-error)',
              '--n-text-color-pressed': 'var(--cp-text-on-error)',
              '--n-text-color-focus': 'var(--cp-text-on-error)',
              '--n-text-color-disabled': 'var(--cp-error-400)',
              '--n-border': '1px solid var(--cp-error-500)',
              '--n-border-hover': '1px solid var(--cp-error-600)',
              '--n-border-pressed': '1px solid var(--cp-error-700)',
              '--n-border-focus': '1px solid var(--cp-error-500)',
              '--n-border-disabled': '1px solid var(--cp-error-100)',
            }
          : props.color === 'success'
            ? {
                '--n-color': 'var(--cp-text-on-primary)',
                '--n-color-hover': 'var(--cp-success-600)',
                '--n-color-pressed': 'var(--cp-success-700)',
                '--n-color-focus': 'var(--cp-success-500)',
                '--n-color-disabled': 'var(--cp-success-100)',
                '--n-text-color': 'var(--cp-success-500)',
                '--n-text-color-hover': 'var(--cp-text-on-primary)',
                '--n-text-color-pressed': 'var(--cp-text-on-primary)',
                '--n-text-color-focus': 'var(--cp-text-on-primary)',
                '--n-text-color-disabled': 'var(--cp-success-400)',
                '--n-border': '1px solid var(--cp-success-500)',
                '--n-border-hover': '1px solid var(--cp-success-600)',
                '--n-border-pressed': '1px solid var(--cp-success-700)',
                '--n-border-focus': '1px solid var(--cp-success-500)',
                '--n-border-disabled': '1px solid var(--cp-success-100)',
              }
            : {
                '--n-color': 'var(--cp-primary-500)',
                '--n-color-hover': 'var(--cp-primary-600)',
                '--n-color-pressed': 'var(--cp-primary-700)',
                '--n-color-focus': 'var(--cp-primary-500)',
                '--n-color-disabled': 'var(--cp-primary-200)',
                '--n-text-color': 'var(--cp-text-on-primary)',
                '--n-text-color-hover': 'var(--cp-text-on-primary)',
                '--n-text-color-pressed': 'var(--cp-text-on-primary)',
                '--n-text-color-focus': 'var(--cp-text-on-primary)',
                '--n-text-color-disabled': 'var(--cp-text-on-primary)',
                '--n-border': '1px solid var(--cp-primary-500)',
                '--n-border-hover': '1px solid var(--cp-primary-600)',
                '--n-border-pressed': '1px solid var(--cp-primary-700)',
                '--n-border-focus': '1px solid var(--cp-primary-500)',
                '--n-border-disabled': '1px solid var(--cp-primary-200)',
              },

      secondary:
        props.color === 'primary'
          ? {
              '--n-color': 'transparent',
              '--n-color-hover': 'var(--cp-primary-100)',
              '--n-color-pressed': 'var(--cp-primary-100)',
              '--n-color-focus': 'var(--cp-primary-100)',
              '--n-color-disabled': 'transparent',
              '--n-text-color': 'var(--cp-primary-700)',
              '--n-text-color-hover': 'var(--cp-primary-700)',
              '--n-text-color-pressed': 'var(--cp-primary-700)',
              '--n-text-color-focus': 'var(--cp-primary-700)',
              '--n-text-color-disabled': 'var(--cp-primary-200)',
              '--n-icon-color-static': 'var(--cp-primary-500)',
              '--n-border': '1px solid var(--cp-primary-400)',
              '--n-border-hover': '1px solid var(--cp-primary-500)',
              '--n-border-pressed': '1px solid var(--cp-primary-500)',
              '--n-border-focus': '1px solid var(--cp-primary-500)',
              '--n-border-disabled': '1px solid var(--cp-primary-200)',
            }
          : props.color === 'success'
            ? {
                '--n-color': 'transparent',
                '--n-color-hover': 'var(--cp-success-50)',
                '--n-color-pressed': 'var(--cp-success-100)',
                '--n-color-focus': 'var(--cp-success-50)',
                '--n-color-disabled': 'transparent',
                '--n-text-color': 'var(--cp-success-500)',
                '--n-text-color-hover': 'var(--cp-success-700)',
                '--n-text-color-pressed': 'var(--cp-success-900)',
                '--n-text-color-focus': 'var(--cp-success-700)',
                '--n-text-color-disabled': 'var(--cp-font-400)',
                '--n-border': '1px solid var(--cp-success-500)',
                '--n-border-hover': '1px solid var(--cp-success-500)',
                '--n-border-pressed': '1px solid var(--cp-success-500)',
                '--n-border-focus': '1px solid var(--cp-success-500)',
                '--n-border-disabled': '1px solid var(--cp-background-600)',
              }
            : props.color === 'error'
              ? {
                  '--n-color': 'var(--cp-background-500)',
                  '--n-color-hover': 'var(--cp-error-50)',
                  '--n-color-pressed': 'var(--cp-error-100)',
                  '--n-color-focus': 'var(--cp-error-50)',
                  '--n-color-disabled': 'transparent',
                  '--n-text-color': 'var(--cp-error-500)',
                  '--n-text-color-hover': 'var(--cp-error-700)',
                  '--n-text-color-pressed': 'var(--cp-error-900)',
                  '--n-text-color-focus': 'var(--cp-error-700)',
                  '--n-text-color-disabled': 'var(--cp-font-400)',
                  '--n-border': '1px solid var(--cp-error-300)',
                  '--n-border-hover': '1px solid var(--cp-error-300)',
                  '--n-border-pressed': '1px solid var(--cp-error-300)',
                  '--n-border-focus': '1px solid var(--cp-error-300)',
                  '--n-border-disabled': '1px solid var(--cp-background-600)',
                }
              : props.color === 'text'
                ? {
                    '--n-color': 'var(--cp-background-500)',
                    '--n-color-hover': 'var(--cp-hover)',
                    '--n-color-pressed': 'var(--cp-hover)',
                    '--n-color-focus': 'var(--cp-hover)',
                    '--n-color-disabled': 'transparent',
                    '--n-text-color': 'var(--cp-font-500)',
                    '--n-text-color-hover': 'var(--cp-font-700)',
                    '--n-text-color-pressed': 'var(--cp-font-900)',
                    '--n-text-color-focus': 'var(--cp-font-700)',
                    '--n-text-color-disabled': 'var(--cp-font-400)',
                    '--n-border': '1px solid var(--cp-border-500)',
                    '--n-border-hover': '1px solid var(--cp-border-600)',
                    '--n-border-pressed': '1px solid var(--cp-border-600)',
                    '--n-border-focus': '1px solid var(--cp-border-600)',
                    '--n-border-disabled': '1px solid var(--cp-background-600)',
                  }
                : props.color === 'transparent'
                  ? {
                      '--n-color': 'none',
                      '--n-color-hover': 'none',
                      '--n-color-pressed': 'none',
                      '--n-color-focus': 'none',
                      '--n-color-disabled': 'none',
                      '--n-text-color': 'var(--cp-font-500)',
                      '--n-text-color-hover': 'none',
                      '--n-text-color-pressed': 'none',
                      '--n-text-color-focus': 'none',
                      '--n-text-color-disabled': 'var(--cp-font-400)',
                      '--n-border': 'none',
                      '--n-border-hover': 'none',
                      '--n-border-pressed': 'none',
                      '--n-border-focus': 'none',
                      '--n-border-disabled': 'none',
                      '--n-box-shadow': '0px 0px 0px 0px',
                    }
                  : {
                      '--n-color': 'transparent',
                      '--n-color-hover': 'var(--cp-border-100)',
                      '--n-color-pressed': 'var(--cp-border-100)',
                      '--n-color-focus': 'var(--cp-border-100)',
                      '--n-color-disabled': 'transparent',
                      '--n-text-color': 'var(--cp-font-700)',
                      '--n-text-color-hover': 'var(--cp-font-900)',
                      '--n-text-color-pressed': 'var(--cp-font-900)',
                      '--n-text-color-focus': 'var(--cp-font-900)',
                      '--n-text-color-disabled': 'var(--cp-font-400)',
                      '--n-border': '1px solid var(--cp-border-500)',
                      '--n-border-hover': '1px solid var(--cp-border-600)',
                      '--n-border-pressed': '1px solid var(--cp-border-600)',
                      '--n-border-focus': '1px solid var(--cp-border-600)',
                      '--n-border-disabled': '1px solid var(--cp-background-600)',
                    },
      tertiary: {
        '--n-text-color-disabled': 'var(--cp-font-400)',
      },
      text:
        props.color === 'primary'
          ? {
              '--n-color': 'transparent',
              '--n-color-hover': 'var(--cp-primary-50)',
              '--n-color-pressed': 'var(--cp-primary-100)',
              '--n-color-focus': 'transparent',
              '--n-color-disabled': 'transparent',
              '--n-text-color': 'var(--cp-primary-500)',
              '--n-text-color-hover': 'var(--cp-primary-600)',
              '--n-text-color-pressed': 'var(--cp-primary-700)',
              '--n-text-color-focus': 'var(--cp-primary-600)',
              '--n-text-color-disabled': 'var(--cp-font-400)',
              '--n-border': 'none',
              '--n-border-hover': 'none',
              '--n-border-pressed': 'none',
              '--n-border-focus': 'none',
              '--n-border-disabled': 'none',
              '--n-box-shadow': 'none',
            }
          : props.color === 'transparent'
            ? {
                '--n-color': 'transparent',
                '--n-color-hover': 'var(--cp-hover)',
                '--n-color-pressed': 'var(--cp-hover)',
                '--n-color-focus': 'transparent',
                '--n-color-disabled': 'transparent',
                '--n-text-color': 'var(--cp-font-500)',
                '--n-text-color-hover': 'var(--cp-font-700)',
                '--n-text-color-pressed': 'var(--cp-font-700)',
                '--n-text-color-focus': 'var(--cp-font-500)',
                '--n-text-color-disabled': 'var(--cp-font-400)',
                '--n-border': 'none',
                '--n-border-hover': 'none',
                '--n-border-pressed': 'none',
                '--n-border-focus': 'none',
                '--n-border-disabled': 'none',
                '--n-box-shadow': 'none',
              }
            : {
                '--n-color': 'transparent',
                '--n-color-hover': 'var(--cp-border-100)',
                '--n-color-pressed': 'var(--cp-border-100)',
                '--n-color-focus': 'transparent',
                '--n-color-disabled': 'transparent',
                '--n-text-color': 'var(--cp-font-500)',
                '--n-text-color-hover': 'var(--cp-font-900)',
                '--n-text-color-pressed': 'var(--cp-font-900)',
                '--n-text-color-focus': 'var(--cp-font-500)',
                '--n-text-color-disabled': 'var(--cp-font-400)',
                '--n-border': 'none',
                '--n-border-hover': 'none',
                '--n-border-pressed': 'none',
                '--n-border-focus': 'none',
                '--n-border-disabled': 'none',
              },
      ghost: {
        '--n-text-color-disabled': 'var(--cp-font-400)',
      },
    } as Record<string, Record<string, string>>

    const typeStyleString = Object.entries(typeStyles[props.type])
      .map(([key, value]) => `${key}: ${value}`)
      .join('; ')

    return `${baseStyles}; ${typeStyleString}`
  })
</script>

<template>
  <HLButton v-bind="$attrs" :id="props.id" :size="props.size" :variant="props.type" :style="buttonStyles" :link="props.link">
    <template v-if="$slots['iconLeft']" #iconLeft>
      <slot name="iconLeft"></slot>
    </template>
    <template v-if="$slots.icon" #icon>
      <slot name="icon"></slot>
    </template>
    <template v-if="$slots['iconRight']" #iconRight>
      <slot name="iconRight"></slot>
    </template>
    <slot></slot>
  </HLButton>
</template>

<style scoped>
  .hr-dialog.hr-modal .hr-dialog__title {
    padding: 0px !important;
  }
</style>

<!--
  HighRise button overrides — unscoped because:
    1. .hr-button can render in teleported surfaces (e.g. inside modals,
       dropdowns, popovers) where scoped CSS cannot reach.
    2. HighRise injects --n-* custom properties as inline styles via its
       JS theme engine, so !important is required to beat inline specificity.
  Targets non-primary, non-error, non-text variants only — primary and
  error variants have intentional solid fills wired up in the buttonStyles
  computed above.
-->
<style>
  /*
   * Hover / pressed background override.
   *
   * CPButton secondary (default color) sets --n-color-hover: var(--cp-border-100)
   * which is 90% white — visibly stark on warm/dark themed backgrounds.
   * Ghost buttons (dots/kebab) inherit NaiveUI defaults that also show white.
   *
   * Override non-primary, non-error, non-text button hover/pressed states to
   * use the semantic --cp-hover token (semi-transparent overlay) so the effect
   * is proportional to whatever the background colour is.
   *
   * Primary, error, and text variants are excluded — they have intentional
   * solid fills or transparent backgrounds.
   */
  .hr-button:not([class*='hr-button--primary']):not([class*='hr-button--error']):not([class*='hr-button--text']) {
    --n-color-hover: var(--cp-hover) !important;
    --n-color-pressed: var(--cp-hover) !important;
    /* Focus ring — use primary glow instead of default white/opaque ring.
       --n-color-focus is intentionally NOT overridden: it controls the button
       fill on focus (not the ring outline), and setting it to cp-hover can
       interfere with border visibility. */
    --n-box-shadow-focus: 0 0 0 3px var(--cp-primary-100) !important;
  }

  /*
   * Disabled state — use opacity rather than a light colour so the button
   * stays recognisable regardless of the theme. CPButton sets --n-color-disabled
   * to a very light shade (e.g. primary-200 for primary buttons) which in warm
   * themes is near-transparent and the text colour (text-on-primary = white)
   * becomes invisible. Opacity-based approach avoids this and works across types.
   *
   * Three selectors cover HighRise naming variants:
   *   .hr-button--disabled  — HighRise-prefixed class (if HR renames it)
   *   .n-button--disabled   — Naive UI class kept by HighRise (same pattern as
   *                           .n-input--disabled used in CPInput.vue)
   *   :disabled             — native HTML attribute, always reliable
   */
  .hr-button.hr-button--disabled,
  .hr-button.n-button--disabled,
  .hr-button:disabled,
  .hr-button[aria-disabled='true'] {
    cursor: not-allowed !important;
    opacity: 1 !important;
  }

  .hr-button.hr-button--disabled *,
  .hr-button.n-button--disabled *,
  .hr-button:disabled *,
  .hr-button[aria-disabled='true'] * {
    color: inherit !important;
  }

  /*
   * Ghost variant — render fully transparent so the surface shows through.
   * HighRise renders ghost as .hr-button--ghost; base color transparent.
   */
  .hr-button.hr-button--ghost {
    --n-color: transparent !important;
    --n-color-disabled: transparent !important;
    --n-border: none !important;
    --n-border-hover: none !important;
    --n-border-focus: none !important;
    --n-border-disabled: none !important;
  }
</style>
